<template>
  <div>
    <app-spinner v-if="!page_loaded"/>
    <template v-else>
      <app-cells>
        <app-button @click.native="onShowModal(null)">Add item</app-button>
        <app-input
          @input.native="onItemTableSearch"
          v-model="filter_search"
          class="input--300"
          type="search"
          placeholder="Search"
          name="search"
        />
      </app-cells>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #h__item><div class="table-default__left">Item</div></template>
        <template #item="{ row }"><div class="table-default__left">{{ row.item.public_name }}</div></template>
        <template #variant="{ row }">{{ row.variant?.public_name || '-' }}</template>
        <template #price="{ row }">{{ row.price || '-' }}</template>
        <template #price_updated_at="{ row }">{{ formatDateTime(row.price_updated_at) }}</template>
        <template #controls="{ row }">
          <div class="table-default__controls">
            <button @click="onShowModal(row)" type="button">
              <img src="@/assets/img/edit-table-icon.svg" alt="Edit">
            </button>
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            @input="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" :limit="40" />
      </app-cells>
      <modal
        name="item_modal"
        :width="400"
        :height="'auto'"
        :scrollable="true"
        :adaptive="true"
        :clickToClose="false"
        draggable=".popup__title"
        class="modal-not-overflow"
      >
        <div class="popup">
          <button @click="$modal.hide('item_modal')" class="popup__close" type="button"></button>
          <div class="popup__title">{{ item_to_action ? 'Update' : 'Create' }} Item</div>
          <div class="popup__body">
            <form @submit.prevent="onCheckForm">
              <template v-if="!item_to_action">
                <app-form-group label="Item" required>
                  <app-select
                    @search="onItemSearch"
                    @input="onChangeItem"
                    v-model="form.item"
                    :options="item_options"
                    placeholder="Item"
                    :error="$v.form.item.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.item.$dirty && !$v.form.item.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Variant">
                  <app-select
                    v-model="form.variant"
                    :reduce="item => item.id"
                    :options="variant_options"
                    :readonly="!variant_options.length"
                    placeholder="Variant"
                  />
                </app-form-group>
              </template>
              <app-form-group label="Price" required>
                <app-fin-number
                  v-model="form.price"
                  placeholder="Price"
                  :error="$v.form.price.$error"
                />
                <template #error>
                  <div v-if="$v.form.price.$dirty && !$v.form.price.required">Required field</div>
                </template>
              </app-form-group>
              <app-cells position="center" :indent="false">
                <app-button ref="submit" type="submit">{{ item_to_action ? 'Save' : 'Create' }}</app-button>
              </app-cells>
            </form>
          </div>
        </div>
      </modal>
    </template>
  </div>
</template>

<script>
import {
  getStorageItems,
  getStorageItemsTypeVariants,
  getStorageSuppliersItemPrices,
  postStorageSuppliersIdItemPrices,
  patchStorageSuppliersIdItemPrices
} from '@/http'
import debounce from 'lodash/debounce'
import { format, parseISO } from 'date-fns'
import { required, requiredIf } from 'vuelidate/lib/validators'

export default {
  name: 'StorageSupplierPricesDetailItems',
  data() {
    return {
      page_loaded: false,
      filter_search: null,
      item_options: [],
      variant_options: [],
      table: {
        items: [],
        columns: ['item', 'variant', 'price', 'price_updated_at', 'controls'],
        options: {
          headings: {
            'item': 'Item',
            'variant': 'Variant',
            'price': 'Price',
            'price_updated_at': 'Updated at',
            'controls': '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 40,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_preloader: false,
      form: {},
      item_to_action: null,
    }
  },
  validations: {
    form: {
      price: { required },
      item: {
        required: requiredIf(function() {
          return !this.item_to_action
        })
      },
    }
  },
  created() {
    this.fetchItems()
  },
  methods: {
    fetchItems() {
      this.table_preloader = true
      getStorageSuppliersItemPrices(this.$route.params.id,
        {
          page: this.pagination.page,
          page_size: 40,
          search: this.filter_search
        })
        .then(response => {
          this.table.items = response.data.results
          this.pagination.pages = response.data.pages
          this.pagination.count = response.data.count
          this.table_preloader = false
          this.page_loaded = true
        })
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      if (this.item_to_action) {
        patchStorageSuppliersIdItemPrices(this.$route.params.id, this.item_to_action, this.form)
          .then(() => {
            this.fetchItems()
            this.$notify({
              type: 'success',
              title: 'Data saved',
            })
            this.$modal.hide('item_modal')
          })
          .catch(() => { this.$refs.submit.preload = false })
      } else {
        postStorageSuppliersIdItemPrices(this.$route.params.id,
          {
            ...this.form,
            item: this.form.item.id,
          })
          .then(() => {
            this.fetchItems()
            this.$notify({
              type: 'success',
              title: 'Data saved',
            })
            this.$modal.hide('item_modal')
          })
          .catch(() => { this.$refs.submit.preload = false })
      }
    },
    onShowModal(row) {
      this.item_to_action = row?.id
      if (!row) {
        this.form = {}
      } else {
        this.form = {
          price: row.price,
        }
      }
      this.$v.form.$reset()
      this.$modal.show('item_modal')
    },
    onChangeItem(e) {
      this.form.variant = null
      this.variant_options = []
      if (e) {
        getStorageItemsTypeVariants(e.id)
          .then(response => {
            this.variant_options = response.data
          })
      }
    },
    formatDateTime(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
    onItemSearch(search, loading) {
      loading(true)
      this.searchItem(loading, search, this)
    },
    searchItem: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.item_options = []
      } else {
        getStorageItems({search}).then(response => {
          vm.item_options = response.data
          loading(false)
        })
      }
    }, 350),
    onItemTableSearch() {
      this.searchItemTable(this)
    },
    searchItemTable: debounce((vm) => {
      vm.pagination.page = 1
      vm.fetchItems()
    }, 350),
  }
}
</script>

